import React from 'react';
import { Card, Button, Badge } from '../../components/ui/Kit';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiCreditCard, FiCheck, FiZap, FiMail } = FiIcons;

const PLAN_FEATURES = {
  starter: ['Up to 3 users', '1,000 leads/month', 'Basic pipeline', 'Email support'],
  growth: ['Up to 15 users', '10,000 leads/month', 'Advanced pipelines', 'Priority support', 'Analytics dashboard'],
  enterprise: ['Unlimited users', 'Unlimited leads', 'Custom pipelines', 'Dedicated support', 'Custom integrations', 'SLA guarantee'],
};

function PlanCard({ tier, label, price, features, isCurrent, isHighlighted }) {
  return (
    <div className={`rounded-xl border-2 p-6 flex flex-col gap-4 transition-all ${
      isCurrent ? 'border-primary bg-primary/5' : isHighlighted ? 'border-gray-300 bg-white' : 'border-gray-200 bg-white'
    }`}>
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{tier}</p>
          <p className="text-xl font-bold text-gray-900 mt-0.5">{label}</p>
        </div>
        {isCurrent && <Badge variant="primary">Current Plan</Badge>}
      </div>
      <p className="text-3xl font-bold text-gray-900">
        {price}<span className="text-sm font-normal text-gray-500">/mo</span>
      </p>
      <ul className="space-y-2 flex-1">
        {features.map((f, i) => (
          <li key={i} className="flex items-center gap-2 text-sm text-gray-600">
            <SafeIcon icon={FiCheck} className="w-4 h-4 text-green-500 flex-shrink-0" />
            {f}
          </li>
        ))}
      </ul>
      {isCurrent ? (
        <div className="text-center text-sm text-primary font-medium py-2">✓ Active</div>
      ) : (
        <Button
          variant="outline"
          className="w-full"
          onClick={() => window.location.href = 'mailto:sales@prosperityleads.com?subject=Upgrade%20Plan'}
        >
          <SafeIcon icon={FiMail} className="mr-2 w-4 h-4" />
          Contact us to upgrade
        </Button>
      )}
    </div>
  );
}

export default function BillingTab() {
  const currentPlan = 'starter';

  return (
    <div className="space-y-6">
      {/* Current Plan Summary */}
      <Card className="p-6">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-primary/10 rounded-xl">
              <SafeIcon icon={FiCreditCard} className="w-6 h-6 text-primary" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Current Plan</p>
              <p className="text-xl font-bold text-gray-900 capitalize">{currentPlan}</p>
              <p className="text-xs text-gray-400 mt-0.5">Renews on July 1, 2025</p>
            </div>
          </div>
          <div className="text-right">
            <p className="text-2xl font-bold text-gray-900">$49<span className="text-sm font-normal text-gray-500">/mo</span></p>
            <Badge variant="success" className="mt-1">Active</Badge>
          </div>
        </div>
      </Card>

      {/* Plan Comparison */}
      <div>
        <div className="flex items-center gap-2 mb-4">
          <SafeIcon icon={FiZap} className="w-4 h-4 text-primary" />
          <h3 className="text-base font-semibold text-gray-900">Available Plans</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <PlanCard
            tier="Starter"
            label="$49/mo"
            price="$49"
            features={PLAN_FEATURES.starter}
            isCurrent={currentPlan === 'starter'}
          />
          <PlanCard
            tier="Growth"
            label="$149/mo"
            price="$149"
            features={PLAN_FEATURES.growth}
            isCurrent={currentPlan === 'growth'}
            isHighlighted
          />
          <PlanCard
            tier="Enterprise"
            label="Custom"
            price="Custom"
            features={PLAN_FEATURES.enterprise}
            isCurrent={currentPlan === 'enterprise'}
          />
        </div>
      </div>

      {/* Contact CTA */}
      <Card className="p-6 bg-gradient-to-r from-primary/5 to-secondary/5 border-primary/20">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <p className="font-semibold text-gray-900">Need a custom plan?</p>
            <p className="text-sm text-gray-500 mt-0.5">We offer tailored pricing for large agencies and enterprise teams.</p>
          </div>
          <Button
            onClick={() => window.location.href = 'mailto:sales@prosperityleads.com?subject=Enterprise%20Plan%20Inquiry'}
          >
            <SafeIcon icon={FiMail} className="mr-2 w-4 h-4" />
            Contact us to upgrade
          </Button>
        </div>
      </Card>
    </div>
  );
}